import { CompanySettings } from "@/types/firebase";

/**
 * Determines whether ACH payments are available for the given company.
 *
 * The logic checks the modern multi-gateway configuration first, falls back to legacy
 * payment flags, and finally verifies that legacy WikiPayit credentials exist.
 */
export function isAchPaymentAvailable(company?: CompanySettings | null): boolean {
  if (!company) {
    return false;
  }

  const legacyPaymentSettings = (company as { payment?: { achEnabled?: boolean } })?.payment;
  if (legacyPaymentSettings?.achEnabled) {
    return true;
  }

  const paymentGateways = company.paymentGateways;
  if (paymentGateways) {
    const providers = paymentGateways.providers || {};
    const isProviderEnabled = (provider?: { enabled?: boolean }) => provider?.enabled ?? false;
    const defaultAch = paymentGateways.defaults?.ach;

    if (defaultAch === "nmi") {
      // Slider explicitly enables NMI for ACH. Ensure credentials exist for legacy support.
      const wikiPayitSettings = (company as { wikiPayitSettings?: { tokenizationKey?: string; securityKey?: string } }).wikiPayitSettings;
      if (wikiPayitSettings?.tokenizationKey && wikiPayitSettings?.securityKey) {
        return true;
      }

      // Fallback to provider toggle if credentials are missing (future-proof, e.g. dedicated ACH flag).
      return isProviderEnabled(providers.nmi);
    }

    if (defaultAch === "payarc") {
      return isProviderEnabled(providers.payarc);
    }

    // No explicit default set – enable ACH if any known provider is enabled.
    return isProviderEnabled(providers.nmi) || isProviderEnabled(providers.payarc);
  }

  const wikiPayitSettings = (company as { wikiPayitSettings?: { tokenizationKey?: string; securityKey?: string } }).wikiPayitSettings;
  if (wikiPayitSettings?.tokenizationKey && wikiPayitSettings?.securityKey) {
    return true;
  }

  return false;
}
